import { Outlet, Link, createRootRoute, HeadContent, Scripts, useRouterState } from "@tanstack/react-router";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { PwaRegistration } from "@/components/PwaRegistration";
import { MarketAddedToCartToaster } from "@/components/market/MarketAddedToCartToaster";
import { AppDialogsProvider } from "@/components/ui/app-dialogs";
import { initSentryClient, setSentryClientTag } from "@/lib/sentry-client";
import appCss from "../styles.css?url";
import structuraBrandCss from "../structura-brand.css?url";

const RETIRED_PUBLIC_ROUTES = [
  "/contratar",
  "/contratar/pendiente",
  "/solicitar-trial",
  "/prueba-gratis",
  "/marketing",
  "/landing",
];

function isRetiredPublicRoute(pathname: string) {
  return RETIRED_PUBLIC_ROUTES.some(
    (route) => pathname === route || (route === "/landing" && pathname.startsWith("/landing/")),
  );
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página no encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          La página que buscás no existe o fue movida.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Ir al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}

// FM is the product workspace. Commercial acquisition and pricing live on structura.com.ar.
export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0a1629" },
      { property: "og:locale", content: "es_AR" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "stylesheet", href: appCss },
      { rel: "stylesheet", href: structuraBrandCss },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es-AR">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const retiredPublicRoute = isRetiredPublicRoute(pathname);

  useEffect(() => {
    initSentryClient();
  }, []);

  useEffect(() => {
    setSentryClientTag("route", pathname);
  }, [pathname]);

  useEffect(() => {
    if (retiredPublicRoute && typeof window !== "undefined") {
      window.location.replace("https://structura.com.ar/");
    }
  }, [retiredPublicRoute]);

  if (retiredPublicRoute) return null;

  return (
    <AppErrorBoundary>
      <AppDialogsProvider>
        <PwaRegistration />
        <Outlet />
        <MarketAddedToCartToaster />
        <Toaster richColors position="top-center" />
      </AppDialogsProvider>
    </AppErrorBoundary>
  );
}
